import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Clipboard, Pencil, Smartphone, Mail, Phone, KeyRound, Fingerprint, CheckCircle2, AlertTriangle, Building2, Calendar, MapPin, MessageSquare, MessageCircle, Tag, Search, Check, Copy, GitMerge, Send, Trash2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button, ConfirmDialog, CopyButton, Spinner } from '@/components/ui';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { TagIconBadge, TagIconRow } from '@/components/tags/TagIconBadge';
import { PatientAvatar } from '@/components/patients/PatientAvatar';
import { MemberBadge, isMemberFromMemberships } from '@/components/patients/MemberBadge';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import {
  deletePatient,
  getDemographics,
  getPatient,
  sendAppInvite,
} from '@/services/patients.service';
import { formatPhoneByCode } from '@/lib/phone';
import { apiClient, getErrorMessage } from '@/services/apiClient';
import { listPatientForms } from '@/services/patientForms.service';
import { usePatientMemberships, usePatientTags, usePatientPrimaryNote, patientQueryKeys } from '@/hooks/usePatientDetail';
import { useModuleAccess } from '@/hooks/useAccountModules';
import { getWhatsAppConsent } from '@/services/whatsappMessaging.service';
import { PATIENTS_MODULE_KEY, PATIENTS_SECTION_APP_STATUS } from '@/pages/patientDetailSections';
import type { PatientSidebarTag } from '@/lib/patientDetail';
import {
  PatientQuickStatsSection,
  EditableNoteSection,
  MembershipsSection,
  UpcomingAppointmentsSection,
  RecentVisitsSection,
  RecentMessagesSection,
} from '@/components/patients/PatientDashboardSections';
import { PatientProductsSection } from '@/components/patients/PatientProductsSection';
import { PatientDemographicsEditModal } from './PatientDemographicsEditModal';
import { PatientMergeModal } from './PatientMergeModal';

/** The date alone; the age sits beside the patient's name. */
function formatDob(birthDate: string): string | null {
  if (!birthDate || birthDate.startsWith('0000')) return null;
  const dob = new Date(birthDate + 'T00:00:00');
  if (isNaN(dob.getTime())) return null;
  return dob.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function genderLabel(gender: string | null | undefined): string {
  if (gender === 'M' || gender === 'm') return 'Male';
  if (gender === 'F' || gender === 'f') return 'Female';
  return gender || '';
}

/**
 * One hue per kind of field, so the icons read apart at a glance. Status colours
 * (Verified, Invalid, consent ticks) are never drawn from here. Whole class
 * strings, so Tailwind's scanner finds them.
 */
const FIELD_TONES = {
  email: { tile: 'bg-blue-50', icon: 'text-blue-600' },
  phone: { tile: 'bg-emerald-50', icon: 'text-emerald-600' },
  dob: { tile: 'bg-violet-50', icon: 'text-violet-600' },
  address: { tile: 'bg-amber-50', icon: 'text-amber-600' },
  location: { tile: 'bg-sky-50', icon: 'text-sky-600' },
  whatsapp: { tile: 'bg-green-50', icon: 'text-green-600' },
  password: { tile: 'bg-amber-50', icon: 'text-amber-600' },
  sso: { tile: 'bg-violet-50', icon: 'text-violet-600' },
  app: { tile: 'bg-indigo-50', icon: 'text-indigo-600' },
  tags: { tile: 'bg-pink-50', icon: 'text-pink-600' },
} as const;
type FieldTone = keyof typeof FIELD_TONES;

/** md for the contact strip, sm for the App Status card, xs inside pills and inline rows. */
const TILE_SIZES = {
  md: { tile: 'h-7 w-7', icon: 'h-3.5 w-3.5' },
  sm: { tile: 'h-6 w-6', icon: 'h-3.5 w-3.5' },
  xs: { tile: 'h-5 w-5', icon: 'h-3 w-3' },
} as const;

/** A field icon in a soft round tile. Round, never rounded-lg: the strip is found by that. */
function IconTile({
  icon: Icon,
  tone,
  size = 'md',
  className,
}: {
  icon: LucideIcon;
  tone: FieldTone;
  size?: keyof typeof TILE_SIZES;
  className?: string;
}) {
  return (
    <span
      className={`flex ${TILE_SIZES[size].tile} shrink-0 items-center justify-center rounded-full ${FIELD_TONES[tone].tile} ${className ?? ''}`}
    >
      <Icon className={`${TILE_SIZES[size].icon} ${FIELD_TONES[tone].icon}`} />
    </span>
  );
}

/** One labelled box in the header's contact strip. An empty value leaves it blank. */
function InfoBox({
  icon,
  tone,
  label,
  marker,
  className,
  children,
}: {
  icon: LucideIcon;
  tone: FieldTone;
  label: string;
  marker?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className={`flex min-w-0 flex-[1_1_auto] gap-2.5 bg-white px-3.5 py-2.5 ${className ?? ''}`}>
      <IconTile icon={icon} tone={tone} className="mt-0.5" />
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
          <span>{label}</span>
          {marker}
        </div>
        <div className="min-h-5 text-sm text-slate-700">{children}</div>
      </div>
    </div>
  );
}

function getAge(birthDate: string | null | undefined): number | null {
  if (!birthDate || birthDate.startsWith('0000')) return null;
  const dob = new Date(birthDate + 'T00:00:00');
  if (isNaN(dob.getTime())) return null;
  return Math.floor((Date.now() - dob.getTime()) / 31557600000);
}

function formatDobForCopy(birthDate: string | null | undefined): string {
  if (!birthDate || birthDate.startsWith('0000')) return '';
  const dob = new Date(birthDate + 'T00:00:00');
  if (isNaN(dob.getTime())) return '';
  const mm = String(dob.getMonth() + 1).padStart(2, '0');
  const dd = String(dob.getDate()).padStart(2, '0');
  const yyyy = dob.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

function statusClasses(kind: 'success' | 'warning' | 'neutral') {
  if (kind === 'success') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (kind === 'warning') return 'border-amber-200 bg-amber-50 text-amber-700';
  return 'border-slate-200 bg-slate-50 text-slate-600';
}

function formatInviteDate(value: string | null | undefined): string | null {
  if (!value || value.startsWith('0000')) return null;
  const d = new Date(value.replace(' ', 'T'));
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** A stored date, shown the way staff read dates rather than as a timestamp. */
function formatConsentDate(raw: string): string {
  const parsed = new Date(raw.replace(' ', 'T'));
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function PatientInfoDemographicsTab({ patientId, onSectionNavigate }: { patientId: number; onSectionNavigate?: (section: string) => void }) {
  const [editOpen, setEditOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [inviteConfirmOpen, setInviteConfirmOpen] = useState(false);
  // "App status" is a section of the always-on Patients module, unchecked per
  // practice in Corp-App. Fail-open while loading, like every other module gate
  // — never flash staff out of a card they are entitled to.
  const { disabledSections: patientsDisabledSections, isLoading: patientsModuleLoading } =
    useModuleAccess(PATIENTS_MODULE_KEY);
  const appStatusHidden = !patientsModuleLoading
    && patientsDisabledSections.includes(PATIENTS_SECTION_APP_STATUS);
  const navigate = useNavigate();
  const [tagsEditing, setTagsEditing] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const [tagSelectorOpen, setTagSelectorOpen] = useState(false);
  const [editingNoteTagCustomId, setEditingNoteTagCustomId] = useState<number | null>(null);
  const [noteValue, setNoteValue] = useState('');
  const [pendingTagIds, setPendingTagIds] = useState<Set<number> | null>(null);
  const [pendingNotes, setPendingNotes] = useState<Map<number, string>>(new Map());
  const [tagsSaving, setTagsSaving] = useState(false);
  const [noteEditorPos, setNoteEditorPos] = useState<{ top: number; left: number } | null>(null);
  const tagSelectorRef = useRef<HTMLDivElement>(null);
  const noteInputRef = useRef<HTMLTextAreaElement>(null);
  const queryClient = useQueryClient();
  const { accountId: authAccountId, isAdmin } = useAuth();
  const [patientLinkCopied, setPatientLinkCopied] = useState(false);

  const accountQuery = useQuery({
    queryKey: ['account-details'],
    queryFn: async () => {
      const r = await apiClient.get<{ data: Record<string, unknown> }>('/account-details');
      return r.data?.data ?? {};
    },
    staleTime: 10 * 60 * 1000,
  });

  const domainEmailReply = String(accountQuery.data?.domainEmailReply ?? '').trim();

  /**
   * Whether this patient agreed to be messaged on WhatsApp, and how.
   *
   * Its OWN query, deliberately not folded into detailQuery below. Two reasons:
   * that one is a Promise.all, so a 422 here -- which is exactly what a practice
   * without WhatsApp gets -- would blank the whole demographics card; and it is
   * invalidated by every demographics save, which would refetch consent for no
   * reason.
   *
   * Gated on `explicit`, not `enabled`: `enabled` is fail-OPEN and true while
   * loading, so it would fire a guaranteed 422 on every chart open for every
   * practice that does not use WhatsApp.
   */
  const whatsAppOn = useModuleAccess('whatsapp').explicit;
  const consentQuery = useQuery({
    queryKey: ['whatsapp-consent', patientId],
    queryFn: () => getWhatsAppConsent(patientId),
    enabled: Number(patientId) > 0 && whatsAppOn,
    staleTime: 60_000,
    retry: false,
  });

  const detailQuery = useQuery({
    queryKey: ['patient-info-demographics', patientId],
    queryFn: async () => {
      const [patient, demographics] = await Promise.all([
        getPatient(patientId),
        getDemographics(patientId),
      ]);
      return { patient, demographics };
    },
  });

  const toast = useToast();
  const inviteMutation = useMutation({
    mutationFn: () => sendAppInvite(patientId),
    onSuccess: () => {
      toast.success('App invite sent.');
      queryClient.invalidateQueries({ queryKey: ['patient-info-demographics', patientId] });
    },
    onError: () => toast.error('Could not send the invite. Please try again.'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deletePatient(patientId),
    onSuccess: () => {
      toast.success('Patient deleted.');
      // The patient is now soft-deleted; drop all cached fragments and leave
      // the detail page (it would otherwise render a deleted record).
      void queryClient.invalidateQueries();
      navigate('/patients');
    },
    // Surfaces the server message, including the "has payments" (409) block.
    onError: (e) => toast.error('Could not delete patient', getErrorMessage(e)),
  });

  const { data: memberships } = usePatientMemberships(patientId ? String(patientId) : null);
  // Shared with the badge in the unified appointment modal — see MemberBadge.
  const isMember = isMemberFromMemberships(memberships);

  const { data: giFormData } = useQuery({
    queryKey: ['patient-forms-list', patientId, false],
    queryFn: () => listPatientForms(patientId, false),
    enabled: patientId > 0,
    staleTime: 30_000,
  });
  const giFormConfigured = giFormData?.giFormConfigured ?? false;
  const giFormUpToDate = giFormData?.giFormUpToDate ?? false;

  const tagsQuery = usePatientTags(patientId ? String(patientId) : null);
  const tags = tagsQuery.data ?? [];

  const { data: primaryNote } = usePatientPrimaryNote(patientId ? String(patientId) : null);

  const allTagsQuery = useQuery({
    queryKey: ['tags-custom-all'],
    queryFn: async () => {
      const r = await apiClient.get<{ data: { tagsCustomID: number; title: string; icon?: string }[] }>('/tags-custom', { params: { active: true } });
      return (r.data?.data ?? []) as { tagsCustomID: number; title: string; icon?: string }[];
    },
    enabled: tagsEditing,
  });

  const serverTagIds = useMemo(() => new Set(tags.map((t) => t.tagsCustomID)), [tags]);
  const activeTagIds = pendingTagIds ?? serverTagIds;

  const hasPendingTagChanges = useMemo(() => {
    if (pendingNotes.size > 0) return true;
    // Text sitting in an open note editor is a change too -- without this the
    // button still reads "Done" and quietly discards what was just typed.
    if (editingNoteTagCustomId !== null) {
      const baseline = pendingNotes.get(editingNoteTagCustomId)
        ?? tags.find((t) => t.tagsCustomID === editingNoteTagCustomId)?.description
        ?? '';
      if (noteValue.trim() !== baseline) return true;
    }
    if (!pendingTagIds) return false;
    if (pendingTagIds.size !== serverTagIds.size) return true;
    for (const id of pendingTagIds) { if (!serverTagIds.has(id)) return true; }
    return false;
  }, [pendingTagIds, pendingNotes, editingNoteTagCustomId, noteValue, tags, serverTagIds]);

  const sortedSelectorTags = useMemo(() => {
    const all = allTagsQuery.data ?? [];
    const q = tagSearch.toLowerCase().trim();
    const filtered = q ? all.filter((t) => t.title.toLowerCase().includes(q)) : all;
    const assigned = filtered.filter((t) => activeTagIds.has(t.tagsCustomID));
    const unassigned = filtered.filter((t) => !activeTagIds.has(t.tagsCustomID));
    assigned.sort((a, b) => a.title.localeCompare(b.title));
    unassigned.sort((a, b) => a.title.localeCompare(b.title));
    return [...assigned, ...unassigned];
  }, [allTagsQuery.data, activeTagIds, tagSearch]);

  const toggleTag = useCallback((tagId: number) => {
    setPendingTagIds((prev) => {
      const current = prev ?? new Set(serverTagIds);
      const next = new Set(current);
      if (next.has(tagId)) next.delete(tagId); else next.add(tagId);
      return next;
    });
  }, [serverTagIds]);

  const saveTagChanges = useCallback(async () => {
    // A note still open in the editor counts as typed: saving the tags is an
    // obvious enough "keep my work" gesture that losing it here reads as a bug.
    const notes = new Map(pendingNotes);
    if (editingNoteTagCustomId !== null) {
      const trimmed = noteValue.trim();
      if (trimmed) notes.set(editingNoteTagCustomId, trimmed);
      else notes.delete(editingNoteTagCustomId);
    }

    const nextTagIds = pendingTagIds ?? serverTagIds;
    if (!pendingTagIds && notes.size === 0) return;
    setTagsSaving(true);
    try {
      const toAdd = [...nextTagIds].filter((id) => !serverTagIds.has(id));
      const toRemove = [...serverTagIds].filter((id) => !nextTagIds.has(id));

      // Notes on tags that already exist server-side are PATCHed by assignment
      // id; notes on tags being created here have no assignment id yet, so they
      // travel in the POST body instead of a second round-trip that used to
      // find nothing to patch and drop the note silently.
      const noteUpdates = [...notes.entries()]
        .filter(([customId]) => serverTagIds.has(customId) && nextTagIds.has(customId))
        .map(([customId, desc]) => {
          const tag = tags.find((t) => t.tagsCustomID === customId);
          return tag?.patientsTagsCustomID
            ? apiClient.patch(`/patients/${patientId}/tags/${tag.patientsTagsCustomID}`, { description: desc })
            : null;
        })
        .filter(Boolean);

      const [added] = await Promise.all([
        Promise.all(toAdd.map(async (tagId) => {
          const res = await apiClient.post<{ data?: { patientsTagsCustomID?: number | null } }>(
            `/patients/${patientId}/tags`,
            { tagId, description: notes.get(tagId) ?? '' },
          );
          return { tagId, patientsTagsCustomID: Number(res.data?.data?.patientsTagsCustomID ?? 0) };
        })),
        Promise.all(toRemove.map((tagId) => apiClient.delete(`/patients/${patientId}/tags/${tagId}`))),
        Promise.all(noteUpdates),
      ]);

      // Update local cache directly so the chips (and their notes) survive the
      // gap between here and the refetch below.
      const removeSet = new Set(toRemove);
      const allAvailable = allTagsQuery.data ?? [];
      queryClient.setQueryData(
        patientQueryKeys.tags(String(patientId)),
        (old: PatientSidebarTag[] | undefined) => {
          const kept = (old ?? [])
            .filter((t) => !removeSet.has(t.tagsCustomID))
            .map((t) => (notes.has(t.tagsCustomID) ? { ...t, description: notes.get(t.tagsCustomID) ?? '' } : t));
          const fresh = added
            .filter(({ tagId }) => !kept.some((t) => t.tagsCustomID === tagId))
            .map(({ tagId, patientsTagsCustomID }) => {
              const match = allAvailable.find((t) => t.tagsCustomID === tagId);
              return {
                patientsTagsCustomID,
                tagsCustomID: tagId,
                title: match?.title ?? `Tag #${tagId}`,
                description: notes.get(tagId) ?? '',
                icon: match?.icon ?? '',
                createDate: '',
                addedByName: '',
              } as PatientSidebarTag;
            });
          return [...kept, ...fresh];
        },
      );

      setPendingTagIds(null);
      setPendingNotes(new Map());
      setEditingNoteTagCustomId(null);
      setNoteEditorPos(null);

      // Background refetch to sync added tags (get server-assigned IDs)
      queryClient.refetchQueries({ queryKey: patientQueryKeys.tags(String(patientId)) }).catch(() => {});
    } finally {
      setTagsSaving(false);
    }
  }, [pendingTagIds, pendingNotes, editingNoteTagCustomId, noteValue, serverTagIds, tags, allTagsQuery.data, patientId, queryClient]);

  const cancelTagEditing = useCallback(() => {
    setTagsEditing(false);
    setTagSearch('');
    setTagSelectorOpen(false);
    setEditingNoteTagCustomId(null);
    setPendingTagIds(null);
    setPendingNotes(new Map());
    setNoteEditorPos(null);
  }, []);

  const displayTags = useMemo(() => {
    if (!pendingTagIds) return tags;
    const kept = tags.filter((t) => pendingTagIds.has(t.tagsCustomID));
    const newIds = [...pendingTagIds].filter((id) => !serverTagIds.has(id));
    const allAvailable = allTagsQuery.data ?? [];
    const added: PatientSidebarTag[] = newIds.map((id) => {
      const match = allAvailable.find((t) => t.tagsCustomID === id);
      return {
        patientsTagsCustomID: 0,
        tagsCustomID: id,
        title: match?.title ?? `Tag #${id}`,
        description: pendingNotes.get(id) ?? '',
        icon: match?.icon ?? '',
        createDate: '',
        addedByName: '',
      };
    });
    return [...kept, ...added];
  }, [tags, pendingTagIds, pendingNotes, serverTagIds, allTagsQuery.data]);

  const saveTagNote = useCallback(async () => {
    if (editingNoteTagCustomId === null) return;
    const trimmed = noteValue.trim();
    const existingTag = tags.find((t) => t.tagsCustomID === editingNoteTagCustomId);
    const isNewTag = !serverTagIds.has(editingNoteTagCustomId);

    if (isNewTag) {
      setPendingNotes((prev) => {
        const next = new Map(prev);
        if (trimmed) next.set(editingNoteTagCustomId, trimmed); else next.delete(editingNoteTagCustomId);
        return next;
      });
      setEditingNoteTagCustomId(null);
      setNoteEditorPos(null);
      return;
    }

    if (existingTag && trimmed === (existingTag.description || '')) {
      setEditingNoteTagCustomId(null);
      setNoteEditorPos(null);
      return;
    }

    if (existingTag) {
      queryClient.setQueryData(
        patientQueryKeys.tags(String(patientId)),
        (old: PatientSidebarTag[] | undefined) =>
          (old ?? []).map((t) =>
            t.tagsCustomID === editingNoteTagCustomId ? { ...t, description: trimmed } : t,
          ),
      );
      setEditingNoteTagCustomId(null);
      setNoteEditorPos(null);
      await apiClient.patch(`/patients/${patientId}/tags/${existingTag.patientsTagsCustomID}`, { description: trimmed });
    }
  }, [editingNoteTagCustomId, noteValue, tags, serverTagIds, patientId, queryClient]);

  useEffect(() => {
    if (!tagsEditing) return;
    function handleClickOutside(e: MouseEvent) {
      if (tagSelectorRef.current && !tagSelectorRef.current.contains(e.target as Node)) {
        setTagSelectorOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [tagsEditing]);

  useEffect(() => {
    if (editingNoteTagCustomId !== null && noteInputRef.current) {
      noteInputRef.current.focus();
    }
  }, [editingNoteTagCustomId]);

  const patient = detailQuery.data?.patient;

  // Each phone formatted by its own stored dial code (blank is +1).
  const formattedCellPhone = patient ? formatPhoneByCode(patient.cellPhone, patient.cellCountryCode) || null : null;
  const formattedHomePhone = patient ? formatPhoneByCode(patient.homePhone, patient.phoneCountryCode) || null : null;

  if (detailQuery.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div className="py-6 text-sm text-red-600">
        Failed to load patient demographics. Please refresh and try again.
      </div>
    );
  }

  const demographics = detailQuery.data.demographics;
  const p = patient!;

  const raw = p as typeof p & {
    birthDate?: string | null;
    gender?: string | null;
    address?: string | null;
    zip?: string | null;
  };

  const avatarName = [p.firstName, p.lastName].filter(Boolean).join(' ') || 'Patient';
  const nickname = (demographics?.nickname ?? p.informalName ?? '').trim() || null;
  const birthDate = raw.birthDate ?? p.dateOfBirth;
  const dobStr = birthDate ? formatDob(birthDate) : null;
  const age = getAge(birthDate);
  const genderAge = [genderLabel(raw.gender), age !== null ? String(age) : '']
    .filter(Boolean)
    .join(' · ');
  const addressLine = [raw.address, p.city, p.state, raw.zip].filter(Boolean).join(', ');

  const portalTone =
    p.portalStatus === 'Registered'
      ? 'success'
      : p.portalStatus === 'Invited'
        ? 'warning'
        : 'neutral';

  const hasEmail = Boolean((p.email ?? '').trim());
  const inviteLabel =
    p.portalStatus === 'Registered' || p.portalStatus === 'Invited'
      ? 'Re-send Invite'
      : 'Send Invite';

  const patientDisplayId = (p as any).displayId ?? (p as any).displayID ?? null;
  const patientLinkLabel = `${p.lastName}, ${p.firstName}${patientDisplayId ? ` (#${patientDisplayId})` : ''}`;
  const patientLinkUrl = `${window.location.origin}/patients/${patientId}`;

  const copyPatientLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const html = `<a href="${patientLinkUrl}"><span>${patientLinkLabel}</span></a>`;
    const blob = new Blob([html], { type: 'text/html' });
    const plainBlob = new Blob([patientLinkLabel], { type: 'text/plain' });
    void navigator.clipboard.write([
      new ClipboardItem({ 'text/html': blob, 'text/plain': plainBlob }),
    ]).then(() => {
      setPatientLinkCopied(true);
      setTimeout(() => setPatientLinkCopied(false), 1500);
    });
  };

  const effectiveAccountId = (p as any).accountId ?? authAccountId ?? 0;
  const commLogEmail = domainEmailReply
    ? `acct${effectiveAccountId}-0-0-${patientId}@${domainEmailReply}`
    : '';

  return (
    <div className="space-y-6">
      {/* Patient Header */}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="space-y-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex min-w-0 items-center gap-4">
              <PatientAvatar
                patientId={patientId}
                photoUrl={p.photoUrl}
                name={avatarName}
                size="xl"
              />

              <div className="flex-1 min-w-0 space-y-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-lg font-semibold text-slate-900">
                    {p.firstName}
                    {nickname && <> &ldquo;{nickname}&rdquo;</>}
                    {p.middleName && <> {p.middleName}</>}
                    {p.lastName && <> {p.lastName}</>}
                  </p>
                  <button
                    type="button"
                    onClick={copyPatientLink}
                    title={patientLinkCopied ? 'Copied!' : 'Copy patient link'}
                    aria-label="Copy patient link"
                    className="inline-flex shrink-0 items-center justify-center rounded p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-300"
                  >
                    {patientLinkCopied ? (
                      <Check className="h-3.5 w-3.5 text-emerald-500" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                  </button>
                  {genderAge && <span className="text-sm text-slate-500">{genderAge}</span>}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <MemberBadge isMember={isMember} />
                  {giFormConfigured && (
                    <span
                      title={giFormUpToDate ? 'General Intake form completed' : 'General Intake form not completed'}
                      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                        giFormUpToDate
                          ? 'border-blue-200 bg-blue-50 text-blue-700'
                          : 'border-amber-200 bg-amber-50 text-amber-700'
                      }`}
                    >
                      <Clipboard className="h-3 w-3" />
                      {giFormUpToDate ? 'GI' : 'GI Needed'}
                    </span>
                  )}
                  <TagIconRow tags={tags} variant="icon" gapClassName="gap-1" />
                </div>
              </div>
            </div>

            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {isAdmin && (
                <Button
                  type="button"
                  size="sm"
                  variant="danger"
                  onClick={() => setDeleteConfirmOpen(true)}
                >
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                  Delete
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => setMergeOpen(true)}
              >
                <GitMerge className="mr-1.5 h-3.5 w-3.5" />
                Merge
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => setEditOpen(true)}
              >
                <Pencil className="mr-1.5 h-3.5 w-3.5" />
                Edit
              </Button>
            </div>
          </div>

          {/* The contact strip. Every box renders whether or not the patient has a
              value, so the strip keeps one shape and a gap reads as "not on file"
              rather than as a missing line. The 1px gap over a slate fill draws
              the dividers however the boxes wrap.

              A wrapping flex row, not a grid with sm:/xl: columns. Those are
              viewport breakpoints, and the card sits beside up to three side
              menus: on a 1440px screen it is about 824px wide, so five forced
              columns cut the e-mail to a few characters and broke the phone and
              date onto several lines. Each box is sized to its content instead
              and drops to the next row when it does not fit, so the boxes follow
              the card's own width. */}
          <div className="flex flex-wrap gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200">
            <InfoBox
              icon={Mail}
              tone="email"
              label="Email"
              marker={
                p.email ? (
                  <>
                    {p.emailVerified && (
                      <span title="Email Verified" className="flex items-center gap-0.5 text-emerald-600">
                        <CheckCircle2 className="h-3 w-3" />
                        Verified
                      </span>
                    )}
                    {p.emailInvalid && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="flex items-center gap-0.5 text-rose-600 cursor-default select-none">
                            <AlertTriangle className="h-3 w-3" />
                            <span className="font-medium">Invalid</span>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" sideOffset={4} className="max-w-xs px-3 py-2">
                          <p className="text-[11px] font-semibold text-white/90 uppercase tracking-wide mb-0.5">
                            Invalid E-mail
                          </p>
                          <p className="text-xs text-white/90 whitespace-pre-wrap">
                            {p.emailInvalidReason
                              ? p.emailInvalidReason
                              : 'The email provider reported this address as undeliverable.'}
                          </p>
                          <p className="text-[10px] text-white/60 mt-1">Excluded from marketing sends.</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </>
                ) : null
              }
            >
              {p.email && (
                <div className="flex items-center gap-1.5">
                  {/* `truncate` has to sit on a real box, not on this flex row: the bare text
                      node becomes an anonymous flex item, so text-overflow never applies to it
                      and the address gets clipped mid-domain with no ellipsis. */}
                  <span className="truncate" title={p.email}>{p.email}</span>
                  <CopyButton value={p.email} label="email" />
                </div>
              )}
            </InfoBox>

            {/* Cell first; the home number stands in only when there is no cell,
                and carries none of the cell's verified/invalid markers. */}
            <InfoBox
              icon={Phone}
              tone="phone"
              label={formattedCellPhone || !formattedHomePhone ? 'Mobile' : 'Home phone'}
              marker={
                formattedCellPhone ? (
                  <>
                    {p.phoneVerified && (
                      <span title="Phone Verified" className="flex items-center gap-0.5 text-emerald-600">
                        <CheckCircle2 className="h-3 w-3" />
                        Verified
                      </span>
                    )}
                    {p.phoneInvalid && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="flex items-center gap-0.5 text-rose-600 cursor-default select-none">
                            <AlertTriangle className="h-3 w-3" />
                            <span className="font-medium">Invalid</span>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" sideOffset={4} className="max-w-xs px-3 py-2">
                          <p className="text-[11px] font-semibold text-white/90 uppercase tracking-wide mb-0.5">
                            Invalid Number
                          </p>
                          <p className="text-xs text-white/90 whitespace-pre-wrap">
                            {p.phoneInvalidReason
                              ? p.phoneInvalidReason
                              : 'The text provider reported this number as undeliverable.'}
                          </p>
                          <p className="text-[10px] text-white/60 mt-1">Excluded from marketing sends.</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </>
                ) : null
              }
            >
              {(formattedCellPhone || formattedHomePhone) && (
                <div className="flex items-center gap-1.5">
                  <span>{formattedCellPhone || formattedHomePhone}</span>
                  <CopyButton value={formattedCellPhone || formattedHomePhone} label="phone" />
                </div>
              )}
            </InfoBox>

            <InfoBox icon={Calendar} tone="dob" label="Date of birth">
              {dobStr && (
                <div className="flex items-center gap-1.5">
                  <span>{dobStr}</span>
                  <CopyButton value={formatDobForCopy(birthDate)} label="date of birth" />
                </div>
              )}
            </InfoBox>

            <InfoBox icon={MapPin} tone="address" label="Address">
              {addressLine && (
                <div className="flex items-start gap-1.5">
                  <span className="min-w-0 break-words">{addressLine}</span>
                  <CopyButton value={addressLine} label="address" className="mt-0.5" />
                </div>
              )}
            </InfoBox>

            <InfoBox icon={Building2} tone="location" label="Location">
              {p.facilityName && <span className="min-w-0 break-words">{p.facilityName}</span>}
            </InfoBox>
          </div>

          {/* Wraps by the card's width for the same reason as the strip: an lg:
              viewport split put the Comm Log address beside the consent groups
              in too little room and broke it mid-address. */}
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="text-xs text-slate-500">Consent</span>
              <div className="flex min-w-0 flex-wrap items-center gap-1.5 rounded-lg bg-slate-50 px-2.5 py-0.5">
                <IconTile icon={Mail} tone="email" size="xs" />
                <span className="text-xs font-medium text-slate-600">Email:</span>
                <span className="text-xs text-slate-500">Transactional</span>
                {(p as any).emailOptIn ? (
                  <span title="Transactional Emails Enabled" className="flex">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  </span>
                ) : (
                  <span className="h-3.5 w-3.5 rounded-full border border-slate-300" title="Transactional Emails Disabled" />
                )}
                <span className="text-xs text-slate-300 mx-0.5">·</span>
                <span className="text-xs text-slate-500">Marketing</span>
                {!p.emailUnsubscribe ? (
                  <span title="Marketing Emails Enabled" className="flex">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  </span>
                ) : (
                  <span className="h-3.5 w-3.5 rounded-full border border-slate-300" title="Marketing Emails Disabled" />
                )}
              </div>
              <div className="flex min-w-0 flex-wrap items-center gap-1.5 rounded-lg bg-slate-50 px-2.5 py-0.5">
                <IconTile icon={MessageSquare} tone="phone" size="xs" />
                <span className="text-xs font-medium text-slate-600">SMS:</span>
                <span className="text-xs text-slate-500">Transactional</span>
                {(p as any).smsOptIn ? (
                  <span title="Transactional SMS Enabled" className="flex">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  </span>
                ) : (
                  <span className="h-3.5 w-3.5 rounded-full border border-slate-300" title="Transactional SMS Disabled" />
                )}
                <span className="text-xs text-slate-300 mx-0.5">·</span>
                <span className="text-xs text-slate-500">Marketing</span>
                {!p.smsUnsubscribe ? (
                  <span title="Marketing SMS Enabled" className="flex">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  </span>
                ) : (
                  <span className="h-3.5 w-3.5 rounded-full border border-slate-300" title="Marketing SMS Disabled" />
                )}
              </div>
              {/* WhatsApp, only for practices that use it.

                  One axis, not two: WhatsApp has no transactional/marketing
                  split, and inventing one to make the row look symmetrical
                  would be a lie about what is stored.

                  A Tooltip rather than a `title`, unlike its siblings, because
                  this one carries provenance -- who recorded it and when --
                  and `title` is unreadable on touch and cannot wrap. A bare
                  tick would be a claim nobody can check. */}
              {whatsAppOn && consentQuery.data && (
                <div className="flex min-w-0 flex-wrap items-center gap-1.5 rounded-lg bg-slate-50 px-2.5 py-0.5">
                  <IconTile icon={MessageCircle} tone="whatsapp" size="xs" />
                  <span className="text-xs font-medium text-slate-600">WhatsApp:</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="flex items-center gap-1.5">
                        <span className="text-xs text-slate-500">Patient agreed</span>
                        {consentQuery.data.optedIn ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                        ) : (
                          <span className="h-3.5 w-3.5 rounded-full border border-slate-300" />
                        )}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" sideOffset={4} className="max-w-xs px-3 py-2">
                      {consentQuery.data.source
                        ? [
                            consentQuery.data.source,
                            consentQuery.data.recordedBy,
                            consentQuery.data.recordedAt
                              ? formatConsentDate(consentQuery.data.recordedAt)
                              : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')
                        : "Not recorded. Ask the patient, then turn on WhatsApp messages under Edit."}
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
            </div>

            {commLogEmail && (
              <p className="text-sm text-slate-500 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
                <span className="flex shrink-0 items-center gap-1.5 whitespace-nowrap">
                  <IconTile icon={Mail} tone="email" size="xs" />
                  Comm Log E-mail:
                </span>
                {/* The address wraps below the label before it ever breaks; it breaks
                    only when the card is narrower than the address itself. It is one
                    unbreakable token, so `break-all` rather than `break-words`. Without
                    min-w-0 its min-content width sets the column floor and stretches the
                    card past the viewport. Same treatment as ContactInfoHeader. */}
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="min-w-0 break-all">{commLogEmail}</span>
                  <CopyButton value={commLogEmail} label="comm log email" className="shrink-0" />
                </span>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Tags */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <IconTile icon={Tag} tone="tags" size="xs" />
          {displayTags.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5">
              {displayTags.map((tag, i) => {
                const hasNote = Boolean(tag.description);
                const isPendingAdd = pendingTagIds !== null && !serverTagIds.has(tag.tagsCustomID);

                const handleClick = tagsEditing
                  ? (e: React.MouseEvent<HTMLSpanElement>) => {
                      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                      const editorWidth = 288;
                      let left = rect.left;
                      if (left + editorWidth > window.innerWidth - 8) left = window.innerWidth - editorWidth - 8;
                      if (left < 8) left = 8;
                      setNoteEditorPos({ top: rect.bottom + 4, left });
                      setEditingNoteTagCustomId(tag.tagsCustomID);
                      setNoteValue(tag.description || '');
                    }
                  : undefined;

                const trailing = hasNote ? (
                  <span className="text-[9px] text-slate-500 ml-0.5">*</span>
                ) : null;

                return (
                  <TagIconBadge
                    key={tag.patientsTagsCustomID || `pending-${tag.tagsCustomID}-${i}`}
                    tag={tag}
                    onClick={handleClick}
                    trailing={trailing}
                    className={isPendingAdd ? 'opacity-60 ring-1 ring-dashed ring-slate-300 rounded-sm' : undefined}
                  />
                );
              })}
            </div>
          ) : (
            <span className="text-xs text-slate-500">No tags</span>
          )}
          {!tagsEditing && (
            <button
              type="button"
              onClick={() => {
                setTagsEditing(true);
                setPendingTagIds(null);
              }}
              className="shrink-0 text-slate-400 hover:text-slate-600 transition-colors"
              title="Edit Tags"
            >
              <Pencil className="h-3 w-3" />
            </button>
          )}
        </div>

        {tagsEditing && editingNoteTagCustomId !== null && noteEditorPos && (() => {
          const editingTag = displayTags.find((t) => t.tagsCustomID === editingNoteTagCustomId);
          if (!editingTag) return null;
          return (
            <div
              className="fixed z-[9999] w-72 rounded-md border border-slate-200 bg-white shadow-lg p-3"
              style={{ top: noteEditorPos.top, left: noteEditorPos.left }}
            >
              <label className="text-[11px] font-medium text-slate-500 mb-1.5 block">Note for &quot;{editingTag.title}&quot;</label>
              <textarea
                ref={noteInputRef}
                value={noteValue}
                onChange={(e) => setNoteValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') { setEditingNoteTagCustomId(null); setNoteEditorPos(null); } }}
                placeholder="Add a note..."
                rows={3}
                className="w-full rounded border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700 placeholder:text-slate-400 focus:border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-300 resize-y"
              />
              <div className="flex justify-end gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => { setEditingNoteTagCustomId(null); setNoteEditorPos(null); }}
                  className="px-2.5 py-1 text-[11px] font-medium text-slate-500 hover:text-slate-700 rounded border border-slate-200 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => saveTagNote()}
                  className="px-2.5 py-1 text-[11px] font-medium text-white bg-blue-500 hover:bg-blue-600 rounded transition-colors"
                >
                  Save
                </button>
              </div>
            </div>
          );
        })()}

        {tagsEditing && (
          <div className="flex items-start gap-3">
            <div className="relative w-64" ref={tagSelectorRef}>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  value={tagSearch}
                  onChange={(e) => {
                    setTagSearch(e.target.value);
                    setTagSelectorOpen(true);
                  }}
                  onFocus={() => setTagSelectorOpen(true)}
                  placeholder={activeTagIds.size > 0 ? `${activeTagIds.size} tag${activeTagIds.size === 1 ? '' : 's'} selected — search...` : 'Search tags...'}
                  className="w-full rounded-md border border-slate-200 bg-white py-1 pl-7 pr-2 text-xs text-slate-700 placeholder:text-slate-400 focus:border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-300"
                />
              </div>
              {tagSelectorOpen && (
                <div className="absolute z-50 mt-1 w-64 max-h-56 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
                  {sortedSelectorTags.length > 0 ? (
                    sortedSelectorTags.map((tag) => {
                      const checked = activeTagIds.has(tag.tagsCustomID);
                      return (
                        <button
                          key={tag.tagsCustomID}
                          type="button"
                          onClick={() => toggleTag(tag.tagsCustomID)}
                          className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                        >
                          <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${checked ? 'border-blue-500 bg-blue-500 text-white' : 'border-slate-300'}`}>
                            {checked && <Check className="h-2.5 w-2.5" />}
                          </span>
                          {tag.title}
                        </button>
                      );
                    })
                  ) : (
                    <div className="px-3 py-1.5 text-xs text-slate-500">No matching tags</div>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {hasPendingTagChanges && (
                <button
                  type="button"
                  onClick={saveTagChanges}
                  disabled={tagsSaving}
                  className="px-3 py-1 text-[11px] font-medium text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-50 rounded transition-colors"
                >
                  {tagsSaving ? 'Saving...' : 'Save Changes'}
                </button>
              )}
              <button
                type="button"
                onClick={cancelTagEditing}
                className="px-3 py-1 text-[11px] font-medium text-slate-500 hover:text-slate-700 rounded border border-slate-200 hover:bg-slate-50 transition-colors"
              >
                {hasPendingTagChanges ? 'Cancel' : 'Done'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Age Alert */}
      {age !== null && (age < 18 || age > 69) && (
        <div className="flex items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
          <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
          <p className="text-sm font-medium text-amber-800">
            This patient is {age} years old.
          </p>
        </div>
      )}

      {/* Stats Row. Without the App Status card the quick stats take the whole
          row, so the two-track grid only applies when the card is showing. */}
      <div className={`grid gap-3 ${appStatusHidden ? '' : 'lg:grid-cols-[1fr_auto]'}`}>
        <PatientQuickStatsSection patientId={patientId} showExtended />

        {/* App Status Card — hidden when Corp-App unchecks the `app-status`
            section of the Patients module for this practice. */}
        {!appStatusHidden && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 min-w-[220px]">
          <div className="flex items-center gap-2 mb-3">
            <IconTile icon={Smartphone} tone="app" size="sm" />
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">App Status</p>
          </div>
          <span className={`inline-block rounded-full border px-2.5 py-1 text-xs font-medium ${statusClasses(portalTone)}`}>
            {p.portalStatus === 'Registered'
              ? 'Registered'
              : p.portalStatus === 'Invited'
                ? 'Invited'
                : 'Not Registered'}
          </span>
          {p.portalStatus === 'Invited' && formatInviteDate(p.portalStatusDate) ? (
            <p className="mt-1 text-[11px] text-slate-400">Invited {formatInviteDate(p.portalStatusDate)}</p>
          ) : null}
          <div className="mt-3 flex items-center gap-4">
            <div className="flex items-center gap-1.5" title={p.emailVerified ? 'Email Verified' : 'Email Not Verified'}>
              <IconTile icon={Mail} tone="email" size="sm" />
              {p.emailVerified ? (
                <span title="Email Verified" className="flex">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                </span>
              ) : (
                <span className="h-3.5 w-3.5 rounded-full border border-slate-300" title="Email Not Verified" />
              )}
            </div>
            <div className="flex items-center gap-1.5" title={p.phoneVerified ? 'Phone Verified' : 'Phone Not Verified'}>
              <IconTile icon={Phone} tone="phone" size="sm" />
              {p.phoneVerified ? (
                <span title="Phone Verified" className="flex">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                </span>
              ) : (
                <span className="h-3.5 w-3.5 rounded-full border border-slate-300" title="Phone Not Verified" />
              )}
            </div>
            <div className="flex items-center gap-1.5" title={p.passwordExists ? 'Password Set' : 'No Password'}>
              <IconTile icon={KeyRound} tone="password" size="sm" />
              {p.passwordExists ? (
                <span title="Password Set" className="flex">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                </span>
              ) : (
                <span className="h-3.5 w-3.5 rounded-full border border-slate-300" title="No Password" />
              )}
            </div>
            {/* SSO (Cognito) identity. A patient can be Registered on this alone,
                with no password, so the two sit side by side. */}
            <div className="flex items-center gap-1.5" title={p.ssoLinked ? 'SSO Linked' : 'No SSO'}>
              <IconTile icon={Fingerprint} tone="sso" size="sm" />
              {p.ssoLinked ? (
                <span title="SSO Linked" className="flex">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                </span>
              ) : (
                <span className="h-3.5 w-3.5 rounded-full border border-slate-300" title="No SSO" />
              )}
            </div>
          </div>
          <div
            className="mt-4"
            title={hasEmail ? undefined : 'Add a valid e-mail address to this patient before sending an invite.'}
          >
            <Button
              type="button"
              size="sm"
              variant="secondary"
              fullWidth
              disabled={!hasEmail || inviteMutation.isPending}
              leftIcon={<Send className="h-3.5 w-3.5" />}
              onClick={() => setInviteConfirmOpen(true)}
            >
              {inviteLabel}
            </Button>
          </div>
        </div>
        )}
      </div>


      {/* Note */}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <EditableNoteSection note={primaryNote ?? ''} patientId={patientId} />
      </div>

      {/* Products */}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <PatientProductsSection patientId={patientId} patientName={avatarName} />
      </div>

      {/* Dashboard Sections
          Below md the two column wrappers go `contents`, so the four cards
          become direct grid items in one column and can be sized as a set.
          That matters for more than tidiness: without an explicit grid-cols-1
          the sub-lg grid falls back to an *implicit* track, which is sized
          `auto` and therefore inherits its items' min-content width. The
          message previews render in `truncate` (white-space: nowrap), whose
          min-content is the whole string, so the track — and every card
          stretched to it — grew to roughly 890px inside a 375px viewport.
          Tailwind's grid-cols-* compile to minmax(0, 1fr); that zero minimum
          is what the lg layout has always had and the phone tier never did.
          `space-y-0` is required alongside `contents`: the `> * + *` margin
          rule still matches through it and would double up with gap-6.
          Every utility here is max-md:, so md+ and print are untouched. */}
      <div className="grid gap-6 max-md:grid-cols-1 max-md:auto-rows-fr lg:grid-cols-2">
        <div className="space-y-6 max-md:contents max-md:space-y-0">
          <div className="rounded-xl border border-slate-200 bg-white p-5 max-md:min-w-0">
            <MembershipsSection patientId={patientId} onSectionNavigate={onSectionNavigate} />
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5 max-md:min-w-0">
            <RecentMessagesSection patientId={patientId} onSectionNavigate={onSectionNavigate} />
          </div>
        </div>
        <div className="space-y-6 max-md:contents max-md:space-y-0">
          <div className="rounded-xl border border-slate-200 bg-white p-5 max-md:min-w-0">
            <UpcomingAppointmentsSection patientId={patientId} onSectionNavigate={onSectionNavigate} />
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5 max-md:min-w-0">
            <RecentVisitsSection patientId={patientId} onSectionNavigate={onSectionNavigate} />
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <PatientDemographicsEditModal
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        patientId={patientId}
      />

      {/* Send App Invite confirmation. Gated too, not just the card: state left
          open from before the gate resolved must not be able to re-open it. */}
      <ConfirmDialog
        isOpen={inviteConfirmOpen && !appStatusHidden}
        onClose={() => setInviteConfirmOpen(false)}
        variant="warning"
        title={p.portalStatus === 'Registered' || p.portalStatus === 'Invited' ? 'Re-send app invite' : 'Send app invite'}
        message={
          <>
            Send an app invite by e-mail to{' '}
            <strong>{avatarName}</strong>
            {p.email ? (
              <>
                {' '}
                at <strong>{p.email}</strong>
              </>
            ) : null}
            ?
          </>
        }
        confirmLabel={inviteLabel}
        onConfirm={async () => {
          await inviteMutation.mutateAsync().catch(() => {});
        }}
      />

      {/* Delete patient confirmation */}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        variant="danger"
        title="Delete patient"
        message={
          <>
            Delete <strong>{avatarName}</strong>? This removes the patient from
            active lists. Patients with recorded payments cannot be deleted.
          </>
        }
        confirmLabel="Delete patient"
        onConfirm={async () => {
          await deleteMutation.mutateAsync().catch(() => {});
        }}
      />

      {/* Merge Modal */}
      <PatientMergeModal
        isOpen={mergeOpen}
        onClose={() => setMergeOpen(false)}
        primaryPatientId={patientId}
        onMerged={(keptPatientId) => {
          // Merge moves data across two patients' worth of cached queries
          // (plans, payments, appointments, memberships, tasks, comm log,
          // tags, notes, etc). Legacy does a full page reload to dump every
          // cached fragment — we mirror that by invalidating ALL queries.
          void queryClient.invalidateQueries();

          // If the current patient was the one merged away, jump to the
          // kept patient's detail page (legacy redirects to
          // patient_details-test.php?patientID={kept}).
          if (keptPatientId !== patientId) {
            navigate(`/patients/${keptPatientId}`, { replace: true });
          }
        }}
      />

    </div>
  );
}
